import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { AuthLayout } from "@/layouts/AuthLayout";
import { authClient } from "@/lib/auth-client";
import { GoogleButton } from "./GoogleButton";
import { t } from "@shared/i18n";

export function authErrorMessage(code?: string, fallback?: string) {
  switch (code) {
    case "INVALID_EMAIL_OR_PASSWORD":
      return t("Adresse e-mail ou mot de passe incorrect.");
    case "EMAIL_NOT_VERIFIED":
      return t("Votre adresse e-mail n'est pas encore confirmée : nous venons de vous envoyer un lien de confirmation. Consultez votre boîte de réception (et les courriers indésirables).");
    case "USER_ALREADY_EXISTS":
    case "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL":
      return t("Un compte existe déjà avec cette adresse.");
    case "PASSWORD_TOO_SHORT":
      return t("Le mot de passe doit contenir au moins 10 caractères.");
    case "INVALID_TOKEN":
      return t("Ce lien n'est plus valide. Faites une nouvelle demande.");
    default:
      return fallback && !/^[A-Z_]+$/.test(fallback) ? fallback : t("Une erreur est survenue. Réessayez.");
  }
}

export function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const next = params.get("next")?.startsWith("/") ? params.get("next")! : "/app";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error } = await authClient.signIn.email({ email, password, rememberMe: remember });
    setLoading(false);
    if (error) {
      if (error.status === 429) return setError(t("Trop de tentatives. Patientez une minute avant de réessayer."));
      return setError(authErrorMessage(error.code, error.message));
    }
    await qc.invalidateQueries({ queryKey: ["me"] });
    navigate(next, { replace: true });
  };

  return (
    <AuthLayout
      title={t("Bon retour.")}
      subtitle={t("Connectez-vous pour retrouver votre Copilote.")}
      footer={
        <>
          {t("Pas encore de compte ?")}{" "}
          <Link to="/register" className="font-medium text-primary hover:underline">
            {t("Créer un compte")}
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <GoogleButton callbackPath={next} />
        <Field label={t("Adresse e-mail")}>
          {(p) => <Input {...p} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <Field label={t("Mot de passe")}>
          {(p) => (
            <Input {...p} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          )}
        </Field>
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 text-muted">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="size-4 accent-[var(--primary)]" />
            {t("Se souvenir de moi")}
          </label>
          <Link to="/forgot-password" className="text-primary hover:underline">
            {t("Mot de passe oublié ?")}
          </Link>
        </div>
        {error && (
          <p role="alert" className="rounded-lg bg-accent-soft px-3 py-2.5 text-sm text-accent">
            {error}
          </p>
        )}
        <Button type="submit" size="lg" className="w-full" loading={loading}>
          {t("Se connecter")}
        </Button>
      </form>
    </AuthLayout>
  );
}
